/**
 * After a delay: who was told, who confirmed, who could not be reached.
 *
 * The requirement asks the clinics manager and the secretary to follow up
 * every moved patient — contacted and confirmed, or unreachable — so that at
 * the end of the call list they know exactly who still has to be tried again.
 * Each row is a one-tap WhatsApp message (there is no SMS gateway; the
 * manager's own phone is the transport) or a call, then one tap to record how
 * it went. The server scopes the list: the whole hospital for the manager, one
 * doctor's patients for a secretary.
 */

import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, EmptyState, ErrorState, Icon, Spinner } from '../../components/common';
import { clinicsAdminApi, operationsApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { formatDateTime } from './labels';

const STATUS = {
  queued: { label: 'لم يُرسل بعد', className: 'border-amber-500/40 bg-amber-500/15 text-amber-100', icon: 'hourglass' },
  sent: { label: 'أُرسلت', className: 'border-sky-500/30 bg-sky-500/10 text-sky-200', icon: 'send' },
  confirmed: { label: 'تم التواصل والتأكيد', className: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-100', icon: 'check-circle-2' },
  unreachable: { label: 'تعذر الوصول', className: 'border-red-500/40 bg-red-500/15 text-red-100', icon: 'phone-off' },
  skipped: { label: 'بلا هاتف', className: 'border-slate-500/30 bg-slate-500/10 text-slate-300', icon: 'phone-off' },
};

const FILTERS = ['queued', 'sent', 'unreachable', 'confirmed', 'skipped'];

export function NoticeTracker({ refreshKey = 0 }) {
  const { success, error: toastError } = useNotifications();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setRows(await operationsApi.notices({}));
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const mark = async (notice, status) => {
    setBusyId(notice.id);
    try {
      await clinicsAdminApi.setNoticeStatus(notice.id, status);
      success(STATUS[status].label);
      await load();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!rows) return <Spinner label="جارٍ تحميل الإخطارات" variant="inline" />;

  const counts = FILTERS.reduce((m, s) => ({ ...m, [s]: rows.filter((r) => r.status === s).length }), {});
  const shown = filter ? rows.filter((r) => r.status === filter) : rows;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setFilter('')}
          aria-pressed={filter === ''}
          className={`min-h-[44px] rounded-xl border px-3 text-sm font-bold ${filter === '' ? 'border-cyan-500/50 bg-cyan-500/15 text-cyan-100' : 'border-white/10 text-slate-400'}`}
        >
          الكل ({rows.length})
        </button>
        {FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            aria-pressed={filter === s}
            className={`flex min-h-[44px] items-center gap-1.5 rounded-xl border px-3 text-sm font-bold ${
              filter === s ? STATUS[s].className : 'border-white/10 text-slate-400'
            }`}
          >
            <Icon name={STATUS[s].icon} size={14} /> {STATUS[s].label} ({counts[s]})
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState icon="bell-off" title="لا توجد إخطارات هنا" message="تظهر هنا رسائل المرضى بعد أي تأجيل للمواعيد." />
      ) : (
        <ul className="space-y-2">
          {shown.map((n) => {
            const meta = STATUS[n.status] || STATUS.queued;
            const busy = busyId === n.id;
            return (
              <li key={n.id} className="glass-panel flex flex-wrap items-center gap-3 rounded-2xl p-3">
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-white">
                    {n.patientName || '—'}
                    <Badge className={meta.className} icon={meta.icon}>
                      {meta.label}
                    </Badge>
                  </p>
                  <p className="mt-0.5 line-clamp-2 text-xs text-slate-400">{n.body}</p>
                  <p className="text-[10px] text-slate-500">
                    {formatDateTime(n.createdAt)}
                    {n.toPhone ? (
                      <span dir="ltr" className="ms-2">
                        {n.toPhone}
                      </span>
                    ) : null}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {n.waUrl && (
                    <a
                      href={n.waUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={() => n.status === 'queued' && mark(n, 'sent')}
                      className="inline-flex min-h-[40px] items-center gap-1.5 rounded-xl bg-emerald-600 px-3 text-xs font-bold text-onaccent hover:bg-emerald-500"
                    >
                      <Icon name="send" size={14} /> واتساب
                    </a>
                  )}
                  {n.telUrl && (
                    <a href={n.telUrl} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-xl bg-white/5 px-3 text-xs font-bold text-slate-200 hover:bg-white/10">
                      <Icon name="phone-call" size={14} /> اتصال
                    </a>
                  )}
                  {n.status !== 'confirmed' && (
                    <Button variant="success" icon="check" loading={busy} onClick={() => mark(n, 'confirmed')}>
                      تم التواصل
                    </Button>
                  )}
                  {!['unreachable', 'confirmed'].includes(n.status) && (
                    <Button variant="danger" icon="phone-off" loading={busy} onClick={() => mark(n, 'unreachable')}>
                      تعذر الوصول
                    </Button>
                  )}
                  {n.status === 'unreachable' && (
                    <Button variant="subtle" icon="rotate-ccw" loading={busy} onClick={() => mark(n, 'queued')}>
                      إعادة المحاولة
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default NoticeTracker;
